import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bot, CalendarCheck, Mic, RotateCcw, Send, Volume2, VolumeX, X } from "lucide-react";
import { toast } from "sonner";
import { SPRING_MODAL } from "@/lib/motion";
import { SCHEDULE } from "@/data/content";
import { scrollToId } from "@/lib/scroll";
import { useAuth } from "@/context/AuthContext";

const HAS_SPEECH_RECOGNITION = typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
const RSVP_RE = /\b(attend|attending|rsvp|join|visit|come to|book|seat|ticket|tickets|pass)\b/i;

const SCHEDULE_DIGEST = SCHEDULE.map((e) => `Day ${e.day} ${e.time} — ${e.title} @ ${e.venue} [${e.category}]`).join("\n");

const istNow = () =>
  new Intl.DateTimeFormat("en-IN", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata" }).format(new Date());

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

let __msgId = 0;
const nextId = () => ++__msgId;

const getStoredName = () => (localStorage.getItem("dosco-name") || "").trim();

const welcomeFor = (name) =>
  name
    ? `Welcome back, ${name}! Chandbagh has been waiting — ask me about the houses, the October 22–24 schedule, or anything Doon.`
    : "Good day, and welcome to Chandbagh! I'm Dosco Bot — ask me about the school's history, the five houses, Founder's Day traditions, or what's on across October 22–24, 2026.";
const SUGGESTIONS = [
  "Tell me about the five houses",
  "What happens on Day 1?",
  "How well do you know Doon lore?",
];

export default function DoscoBot() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [sid, setSid] = useState(() => localStorage.getItem("dosco-sid") || `s-${Math.random().toString(36).slice(2)}`);
  const [messages, setMessages] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("dosco-chat") || "null");
      if (Array.isArray(saved) && saved.length) return saved.map((m) => ({ ...m, id: nextId() }));
    } catch {}
    return [{ id: nextId(), role: "bot", text: welcomeFor(getStoredName()) }];
  });
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [voice, setVoice] = useState(() => localStorage.getItem("dosco-voice") === "on");
  const [listening, setListening] = useState(false);
  const recogRef = useRef(null);
  const scroller = useRef(null);
  const greetedRef = useRef(false);

  const speak = (text) => {
    if (!("speechSynthesis" in window) || !text) return;
    const u = new SpeechSynthesisUtterance(text.replace(/\s+/g, " ").slice(0, 300));
    const voices = window.speechSynthesis.getVoices();
    const v =
      voices.find((x) => x.lang === "en-IN") ||
      voices.find((x) => x.lang === "en-GB") ||
      voices.find((x) => x.lang?.startsWith("en"));
    if (v) u.voice = v;
    u.rate = 0.98;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  };

  const toggleVoice = () => {
    setVoice((v) => {
      const nv = !v;
      localStorage.setItem("dosco-voice", nv ? "on" : "off");
      if (!nv) window.speechSynthesis?.cancel();
      return nv;
    });
  };

  useEffect(() => () => window.speechSynthesis?.cancel?.(), []);

  const startListening = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    if (listening) {
      recogRef.current?.stop();
      return;
    }
    window.speechSynthesis?.cancel?.();
    const r = new SR();
    recogRef.current = r;
    r.lang = "en-IN";
    r.interimResults = true;
    r.maxAlternatives = 1;
    r.onresult = (e) => {
      let interim = "";
      let final = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) final += t;
        else interim += t;
      }
      if (final.trim()) {
        setInput("");
        try { r.stop(); } catch {}
        send(final.trim());
      } else {
        setInput(interim);
      }
    };
    r.onend = () => setListening(false);
    r.onerror = () => {
      setListening(false);
      toast.error("Couldn't hear that — please try again");
    };
    try {
      r.start();
      setListening(true);
    } catch {
      setListening(false);
    }
  };

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    try {
      localStorage.setItem("dosco-sid", sid);
      localStorage.setItem("dosco-chat", JSON.stringify(messages.slice(-20).map(({ id, kind, rsvp, ...rest }) => rest)));
    } catch {}
  }, [sid, messages]);

  useEffect(() => {
    if (greetedRef.current) return;
    if (!open && !user) return;
    greetedRef.current = true;
    const name = user?.name?.split(" ")[0] || getStoredName();
    setMessages((m) => {
      if (m.some((x) => x.kind === "wb")) return m;
      if (m.length <= 1) return name ? [{ ...m[0], text: welcomeFor(name), kind: "wb" }] : m;
      return [...m, { id: nextId(), role: "bot", text: welcomeFor(name), kind: "wb" }];
    });
  }, [open, user]);

  const send = async (raw) => {
    const text = (raw ?? input).trim();
    if (!text || streaming) return;
    const nm = text.match(/\b(?:my name is|call me)\s+([a-zA-Z][a-zA-Z ]{1,29})/i);
    if (nm) {
      const nice = nm[1]
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ");
      localStorage.setItem("dosco-name", nice);
    }
    setInput("");
    setStreaming(true);
    window.speechSynthesis?.cancel?.();
    const prior = messages.filter((m) => m.text).slice(-8).map((m) => ({ role: m.role, text: m.text }));
    const wantsRsvp = RSVP_RE.test(text);
    setMessages((m) => [...m, { id: nextId(), role: "user", text }, { id: nextId(), role: "bot", text: "", rsvp: wantsRsvp }]);
    try {
      const res = await fetch(`${API_URL}/dosco/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid, message: text, history: prior, schedule: SCHEDULE_DIGEST, time_ist: istNow() }),
      });
      if (!res.ok) throw new Error("dosco failed");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      let full = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          const j = JSON.parse(payload);
          if (j.delta) {
            full += j.delta;
            setMessages((m) => {
              const next = [...m];
              const last = next[next.length - 1];
              next[next.length - 1] = { ...last, text: last.text + j.delta };
              return next;
            });
          }
        }
      }
      if (voice) speak(full);
    } catch (e) {
      setMessages((m) => {
        const next = [...m];
        if (!next[next.length - 1].text) next[next.length - 1].text = "The valley archives seem momentarily out of reach — please ask me again.";
        return next;
      });
      toast.error("Dosco Bot could not reply — please try again");
    } finally {
      setStreaming(false);
    }
  };

  const reset = () => {
    window.speechSynthesis?.cancel?.();
    recogRef.current?.stop?.();
    setListening(false);
    const nsid = `s-${Math.random().toString(36).slice(2)}`;
    setSid(nsid);
    localStorage.setItem("dosco-sid", nsid);
    localStorage.removeItem("dosco-chat");
    const name = user?.name?.split(" ")[0] || getStoredName();
    setMessages([{ id: nextId(), role: "bot", text: welcomeFor(name), kind: "wb" }]);
  };

  return (
    <>
      <motion.button
        onClick={() => setOpen((o) => !o)}
        whileHover={{ scale: 1.08, rotate: open ? 0 : -4 }}
        whileTap={{ scale: 0.92 }}
        transition={SPRING_MODAL}
        aria-label="Chat with Dosco Bot"
        data-testid="dosco-bot-fab"
        className="fixed bottom-6 right-6 z-[70] flex h-14 w-14 items-center justify-center rounded-full bg-gold text-navy shadow-[0_10px_36px_rgba(212,175,55,0.45)]"
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={open ? "x" : "bot"}
            initial={{ opacity: 0, rotate: -60, scale: 0.5 }}
            animate={{ opacity: 1, rotate: 0, scale: 1 }}
            exit={{ opacity: 0, rotate: 60, scale: 0.5 }}
            transition={{ duration: 0.2 }}
            className="flex"
          >
            {open ? <X className="h-6 w-6" /> : <Bot className="h-6 w-6" />}
          </motion.span>
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 32, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={SPRING_MODAL}
            className="glass fixed bottom-24 right-6 z-[70] flex h-[540px] max-h-[calc(100vh-8rem)] w-[min(92vw,380px)] flex-col overflow-hidden rounded-3xl"
            data-testid="dosco-bot-panel"
          >
            <div className="flex items-center gap-3 bg-crimson/90 px-4 py-3.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gold text-navy">
                <Bot className="h-5 w-5" />
              </span>
              <span className="flex-1 leading-tight">
                <span className="block font-display text-sm font-bold tracking-[0.18em] text-gold gold-on-crimson">DOSCO BOT</span>
                <span className="flex items-center gap-1.5 font-mono text-[10px] tracking-widest text-ivory/70">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-ivory" />
                  AT YOUR SERVICE • CHANDBAGH
                </span>
              </span>
              <button onClick={toggleVoice} aria-label={voice ? "Disable voice replies" : "Enable voice replies"} data-testid="dosco-bot-voice" className="rounded-full p-2 text-ivory/70 transition-colors hover:text-gold">
                {voice ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
              </button>
              <button onClick={reset} aria-label="New chat" data-testid="dosco-bot-reset" className="rounded-full p-2 text-ivory/70 transition-colors hover:text-gold">
                <RotateCcw className="h-4 w-4" />
              </button>
              <button onClick={() => setOpen(false)} aria-label="Close chat" data-testid="dosco-bot-close" className="rounded-full p-2 text-ivory/70 transition-colors hover:text-gold">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div ref={scroller} className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="dosco-bot-messages">
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  {m.role === "user" ? (
                    <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-gold px-4 py-2.5 text-sm leading-relaxed text-navy" data-testid="dosco-bot-msg-user">
                      {m.text}
                    </div>
                  ) : (
                    <div className="max-w-[90%]">
                      <div className="glass whitespace-pre-wrap rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm leading-relaxed text-fg" data-testid="dosco-bot-msg">
                        {m.text || (
                          <span className="flex items-center gap-1 py-1" data-testid="dosco-bot-typing">
                            {[0, 1, 2].map((d) => (
                              <span key={d} className="h-1.5 w-1.5 animate-bounce rounded-full bg-gold" style={{ animationDelay: `${d * 0.15}s` }} />
                            ))}
                          </span>
                        )}
                      </div>
                      {m.rsvp && m.text && (
                        <motion.button
                          initial={{ opacity: 0, y: 8, scale: 0.94 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          transition={{ delay: 0.3, type: "spring", stiffness: 250, damping: 18 }}
                          onClick={() => {
                            setOpen(false);
                            setTimeout(() => scrollToId("#guestbook"), 140);
                          }}
                          data-testid="dosco-bot-rsvp"
                          className="mt-2 flex items-center gap-2 rounded-full bg-gold px-4 py-2 font-display text-[10px] font-bold tracking-[0.18em] text-navy shadow-[0_6px_24px_rgba(212,175,55,0.35)] transition-transform duration-300 hover:scale-105"
                        >
                          <CalendarCheck className="h-3.5 w-3.5" /> RSVP NOW — RESERVE YOUR SEAT
                        </motion.button>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {messages.length <= 1 && (
                <div className="flex flex-col gap-2 pt-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      data-testid="dosco-bot-suggestion"
                      className="glass rounded-full px-4 py-2 text-left text-xs text-soft transition-all duration-300 hover:scale-[1.02] hover:border-gold/40 hover:text-gold"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-2 border-t border-gold/15 p-3"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about Chandbagh…"
                maxLength={500}
                data-testid="dosco-bot-input"
                className="glass flex-1 rounded-full px-4 py-2.5 text-sm text-fg placeholder:text-soft focus:border-gold/50 focus:outline-none"
              />
              {HAS_SPEECH_RECOGNITION && (
                <button
                  type="button"
                  onClick={startListening}
                  aria-label={listening ? "Stop listening" : "Ask by voice"}
                  data-testid="dosco-bot-mic"
                  className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-all duration-300 ${listening ? "bg-crimson text-ivory shadow-[0_0_24px_rgba(139,0,0,0.5)]" : "glass text-soft hover:text-gold"}`}
                >
                  {listening && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-crimson opacity-60" />}
                  <Mic className="relative h-4 w-4" />
                </button>
              )}
              <button
                type="submit"
                disabled={streaming || !input.trim()}
                aria-label="Send message"
                data-testid="dosco-bot-send"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold text-navy transition-all duration-300 hover:scale-105 disabled:opacity-40"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
